import { CountBadge } from "@c/shared/CountBadge";
import { cn } from "@u/cn";
import type { ComponentType, KeyboardEvent, ReactNode } from "react";
import { useRef } from "react";

export type SegmentedOption<T extends string> = {
  value: T;
  label: ReactNode;
  /** Optional leading icon (lucide component). */
  icon?: ComponentType<{ size?: number | string }>;
  /** Optional trailing count badge. `null` = still loading (badge hidden). */
  count?: number | null;
  disabled?: boolean;
};

const SIZE = {
  sm: "min-h-11 sm:min-h-8 px-3 text-xs gap-1.5",
  md: "min-h-11 sm:min-h-9 px-3.5 text-sm gap-2",
} as const;

/**
 * THE tab bar of the app — a bordered track with the active option as a filled
 * pill (`bg-foreground text-background` — neutral, not brand blue). Single source for every
 * "switch between mutually exclusive panels/views" control across landing,
 * dashboard and venue-editor: day-range filters, product segments, section
 * tabs, KDS stations, view pickers.
 *
 * Not the same as `SegChip`/`SegGroup` (bordered chips, no track) — those are
 * for *filters* (often multi-select). If exactly one option is always active
 * and it swaps the panel below, it is a SegmentedTabs.
 *
 * a11y: real `tablist`/`tab` semantics with roving tabindex + Arrow/Home/End
 * keyboard nav. Pass `idPrefix` to also wire `aria-controls` to your panels
 * (`<div id={`${idPrefix}-panel-${value}`} role="tabpanel">`).
 */
export function SegmentedTabs<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  layout = "fill",
  selectOnHover = false,
  label,
  idPrefix,
  className,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  /** `fill` = equal-width options filling the row; `auto` = hug content. */
  layout?: "fill" | "auto";
  /** Pointer hover also switches the panel (marketing surfaces). Click still works. */
  selectOnHover?: boolean;
  /** Accessible name of the tablist. */
  label?: string;
  /** When set, emits `id`/`aria-controls` pairs for your tabpanels. */
  idPrefix?: string;
  className?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function select(i: number) {
    const opt = options[i];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    refs.current[i]?.focus();
  }

  function onKeyDown(e: KeyboardEvent) {
    const i = options.findIndex((o) => o.value === value);
    const n = options.length;
    let next = i;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      next = (i - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    else return;
    e.preventDefault();
    select(next);
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        "flex items-center gap-1 p-1 rounded-xl border border-border/40 bg-muted/10 overflow-x-auto custom-scrollbar",
        layout === "auto" ? "w-fit" : "w-full sm:w-fit",
        className,
      )}
    >
      {options.map((opt, i) => {
        const active = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${opt.value}` : undefined}
            aria-selected={active}
            aria-controls={
              idPrefix ? `${idPrefix}-panel-${opt.value}` : undefined
            }
            tabIndex={active ? 0 : -1}
            disabled={opt.disabled}
            onClick={() => select(i)}
            onMouseEnter={
              selectOnHover && !opt.disabled
                ? () => onChange(opt.value)
                : undefined
            }
            onKeyDown={onKeyDown}
            className={cn(
              "flex min-w-0 items-center justify-center rounded-lg font-medium whitespace-nowrap transition-colors disabled:opacity-50",
              layout === "fill" ? "flex-1 sm:flex-none" : "shrink-0",
              SIZE[size],
              active
                ? "bg-foreground text-background shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {Icon && <Icon size={size === "sm" ? 12 : 14} />}
            {opt.label}
            {opt.count != null && (
              <CountBadge
                count={opt.count}
                pad={1}
                className={cn(active && "text-background/70")}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
